import Button from "shared/components/Button";
import SkippyIconCircle from "assets/skippy-icon-circle.png";
import { ReactComponent as SkippyLogo } from "assets/skippy-logo.svg";
import { Suspense, useRef } from "react";
import { Transition } from "react-transition-group";
import { TransitionProps } from "react-transition-group/Transition";
import { ReactComponent as CrossSVG } from "assets/icons/cross.svg";
import Spinner from "../Spinner";

/**
 * No delay for enter
 * `$transitionMedium` in
 * `$transitionShortMedium` out,
 */
const TIMEOUTS: TransitionProps["timeout"] = {
  enter: 0,
  appear: 600,
  exit: 450,
};

export interface PanelProps {
  showPanel: boolean;
  direction: "left" | "right" | "top" | "bottom";
  /**
   * NOTE: This only applies to left/right panels since top/bottom cannot keep
   * the bottom menu in view.
   */
  retainBottomMenu?: boolean;
  /**
   * A border radius was added to the panel in some designs
   * The client later asked that this be removed for all panels, this boolean
   * retains that logic in case specific panels need it in the future (it's
   * likely this will be reinstated for bottom panels at some point).
   */
  withBorderRadius?: boolean;
  closePanel(): void;
  headerElement?: React.ReactElement;
  includeBaseHeader?: boolean;
  headerBorder: "BorderBottom" | "NoBorderBottom";
  panelSize?: "normal" | "large" | "full";
  underlayClass?: "FaintUnderlay" | "ClearUnderlay" | "NoUnderlay";
  scrollClass?: "ScrollAllInner" | "ScrollContent";
  contentClass?: string;
}

/**
 * A standardised off-screen panel component that slides into view.
 */
const Panel: React.FC<React.PropsWithChildren<PanelProps>> = ({
  showPanel,
  direction,
  retainBottomMenu = false,
  withBorderRadius = false,
  closePanel,
  headerElement,
  includeBaseHeader = false,
  headerBorder,
  panelSize = "normal",
  underlayClass = "",
  scrollClass = "ScrollContent",
  contentClass = "",
  children,
}) => {
  const nodeRef = useRef<HTMLDivElement>(null);
  return (
    <Transition
      nodeRef={nodeRef}
      in={showPanel}
      timeout={TIMEOUTS}
      mountOnEnter
      unmountOnExit
    >
      {(transitionState) => (
        <>
          {/*
            Underlay
            This always mounts to allow it to close the panel
            (even if it's colour makes it invisible)
          */}
          <div
            className={`SharedUnderlay ${underlayClass} ${transitionState}`}
            onMouseDown={closePanel}
          />
          {/* Panel Content */}
          <div
            ref={nodeRef}
            className={`Panel ${getPanelDirection(direction)} ${getPanelSize(
              panelSize
            )} ${transitionState} ${scrollClass} ${
              retainBottomMenu ? "ShowBottomMenu" : ""
            } ${withBorderRadius ? "WithBorderRadius" : ""}`}
          >
            <div className="Inner">
              <div className={`Top ${headerBorder}`}>
                {includeBaseHeader && (
                  <div className="BaseHeader">
                    <img src={SkippyIconCircle} height={24} width={24} alt="" />
                    {/*
                      NOTE:
                        Value based on ViewBox dimensions `0 0 1638 478`
                        (width, height)

                        Top of logo missing due to screen resolution, zooming
                        in will show the rest of it.
                    */}
                    <SkippyLogo height={30} width={102.8} />
                  </div>
                )}
                {headerElement && (
                  <div className="HeaderElement">{headerElement}</div>
                )}
                <Button
                  additionalClassNames="Cross NoBorder NoBackground"
                  onClick={closePanel}
                  children={
                    <CrossSVG
                      height={16}
                      width={16}
                      role="img"
                      aria-label="Close Panel"
                      title="Close Panel"
                    />
                  }
                />
              </div>
              <Suspense
                fallback={<Spinner show />}
                children={
                  <div className={`Content ${contentClass}`}>{children}</div>
                }
              />
            </div>
          </div>
        </>
      )}
    </Transition>
  );
};

const getPanelDirection = (direction: PanelProps["direction"]) => {
  switch (direction) {
    case "left":
      return "FromLeft";
    case "right":
      return "FromRight";
    case "top":
      return "FromTop";
    case "bottom":
      return "FromBottom";
    default:
      return "FromLeft";
  }
};
const getPanelSize = (panelSize: PanelProps["panelSize"]) => {
  switch (panelSize) {
    case "large":
      return "LargePanel";
    case "full":
      return "FullSize";
    case "normal":
    default:
      return "";
  }
};

export default Panel;
